/**
 * authBootstrap.ts — attaches the Supabase access token to EVERY request
 * that goes to our API server (fetch, default axios, and every axios.create()
 * instance). The server now verifies identity from this token instead of
 * trusting the "user-id" header. Must be the FIRST import in index.tsx.
 */
import axios from "axios";
import { supabase } from "./supabaseClient";

const SERVER_URL = (process.env.REACT_APP_SERVER_URL || "").replace(/\/$/, "");

function isOurApi(url: string): boolean {
  if (!url) return false;
  if (url.startsWith("/api")) return true;
  if (SERVER_URL && url.startsWith(SERVER_URL)) return true;
  try {
    return new URL(url, window.location.origin).origin === window.location.origin
      && new URL(url, window.location.origin).pathname.startsWith("/api");
  } catch { return false; }
}

async function getToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

// fetch
const originalFetch = window.fetch.bind(window);
window.fetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
  const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
  if (isOurApi(url)) {
    const token = await getToken();
    if (token) {
      const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
      if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
      init = { ...init, headers };
    }
  }
  return originalFetch(input, init);
};

// axios
function attach(instance: any) {
  instance.interceptors.request.use(async (config: any) => {
    const full = `${config.baseURL || ""}${config.url || ""}`;
    if (isOurApi(full) || isOurApi(config.url || "")) {
      const token = await getToken();
      if (token) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });
  return instance;
}
attach(axios);
const originalCreate = axios.create.bind(axios);
(axios as any).create = (cfg?: any) => attach(originalCreate(cfg));

export {};
